import { useEffect } from "react";
import { Pressable, View } from "react-native";
import { X } from "lucide-react-native";
import { ThinkingOrb } from "thinking-orbs";
import type { ThinkingOrbStatusProps } from "./ThinkingOrbStatus.types";

const SHIMMER_STYLE_ID = "louise-thinking-orb-shimmer";

function ensureShimmerStyles() {
  if (
    typeof document === "undefined" ||
    document.getElementById(SHIMMER_STYLE_ID)
  ) {
    return;
  }

  const style = document.createElement("style");
  style.id = SHIMMER_STYLE_ID;
  style.textContent = `
    @keyframes louise-thinking-orb-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: 0 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .louise-thinking-orb-shimmer-overlay { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
}

function ShimmerLabel({
  label,
  hero,
  theme,
}: {
  label: string;
  hero: boolean;
  theme: "light" | "dark";
}) {
  const inkColor = theme === "dark" ? "#FFFFFF" : "#2A2522";
  const labelColor =
    theme === "dark" ? "rgba(255, 255, 255, 0.82)" : "rgba(42, 37, 34, 0.62)";

  return (
    <span
      style={{
        color: labelColor,
        display: "inline-block",
        fontFamily: "DM Sans, sans-serif",
        fontSize: hero ? 12 : 11,
        fontWeight: 700,
        lineHeight: hero ? "16px" : "14px",
        position: "relative",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <span
        aria-hidden="true"
        className="louise-thinking-orb-shimmer-overlay"
        style={{
          backgroundImage: `linear-gradient(90deg, transparent 0%, transparent 35%, ${inkColor} 50%, transparent 65%, transparent 100%)`,
          backgroundPosition: "100% 0",
          backgroundSize: "400% 100%",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          inset: 0,
          pointerEvents: "none",
          position: "absolute",
          WebkitTextFillColor: "transparent",
          animation: "louise-thinking-orb-shimmer 2s linear infinite",
        }}
      >
        {label}
      </span>
    </span>
  );
}

/** Thinking Orbs adapter using the reference package's canvas and light palette. */
export function ThinkingOrbStatus({
  state,
  label,
  size = 20,
  variant = "inline",
  theme = "light",
  surface = "default",
  onCancel,
  accessibilityLabel,
}: ThinkingOrbStatusProps) {
  useEffect(() => {
    ensureShimmerStyles();
  }, []);

  const isHero = variant === "hero";
  const labelText = label == null ? "Activity" : String(label);
  const hasSurface = surface !== "none";

  return (
    <View
      accessibilityLabel={accessibilityLabel ?? labelText}
      accessibilityRole="progressbar"
      style={{
        alignItems: "center",
        backgroundColor:
          !hasSurface || isHero ? "transparent" : "rgba(255, 255, 255, 0.66)",
        borderColor:
          !hasSurface || isHero ? "transparent" : "rgba(42, 37, 34, 0.09)",
        borderRadius: 999,
        borderWidth: !hasSurface || isHero ? 0 : 1,
        flexDirection: isHero ? "column" : "row",
        gap: isHero ? 3 : 7,
        justifyContent: "center",
        minHeight: !hasSurface || isHero ? undefined : 30,
        paddingHorizontal: !hasSurface || isHero ? 0 : 8,
        paddingVertical: !hasSurface || isHero ? 0 : 4,
      }}
    >
      <ThinkingOrb
        aria-label={accessibilityLabel ?? labelText}
        size={size}
        state={state}
        theme={theme}
        style={{ flexShrink: 0 }}
      />
      {label != null ? (
        <ShimmerLabel hero={isHero} label={labelText} theme={theme} />
      ) : null}
      {onCancel ? (
        <Pressable
          accessibilityLabel="Cancel upload"
          accessibilityRole="button"
          hitSlop={6}
          onPress={onCancel}
          style={{
            alignItems: "center",
            borderRadius: 10,
            height: 20,
            justifyContent: "center",
            marginLeft: 1,
            width: 20,
          }}
        >
          <X
            color={
              theme === "dark"
                ? "rgba(255, 255, 255, 0.72)"
                : "rgba(42, 37, 34, 0.58)"
            }
            size={13}
            strokeWidth={2}
          />
        </Pressable>
      ) : null}
    </View>
  );
}

export default ThinkingOrbStatus;
